import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  Card,
  CardContent,
  Stack,
  Chip,
  Fade,
  Slide,
  Zoom,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import {  
  Security,
  Analytics,
  Dashboard,
  Group,
  TrendingUp,
  CheckCircle,
  AccountTree,
  Schedule,
  NoteAdd,
  Close,
  PlayArrow,
  Smartphone
} from '@mui/icons-material';
import TempLogo from '../../assets/svgs/temp-logo.svg';
import Footer from '../shared/Footer';
import ContactForm from '../shared/ContactForm';
import TrackEveryStepIcon from '../../assets/svgs/TrackEveryStepIcon';
import styles from './LandingPage.module.scss';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleBuildPlatformClick = () => {
    navigate('/build-platform');
  };

  const handleAskQuestionClick = () => {
    navigate('/ask-question');
  };

  const handleOpenContactDialog = () => {
    setContactDialogOpen(true);
  };

  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
  };

  const handleOpenVideoDialog = () => {
    setVideoDialogOpen(true);
  };

  const handleCloseVideoDialog = () => {
    setVideoDialogOpen(false);
  };

  const handleContactFormSuccess = (response) => {
    // Close dialog after successful submission
    setTimeout(() => {
      setContactDialogOpen(false);
    }, 2000);
  };

  const handleContactFormError = (error) => {
    console.error('Contact form submission failed:', error);
    // Keep dialog open on error so user can try again
  };

  const additionalTools = [
    {
      icon: <AccountTree />,
      title: 'Transfer Status Tracking',
      description: 'Monitor admission outcomes and subsequent transfers across all categories.'
    },
    {
      icon: <Schedule />,
      title: 'Time Frame Converter',
      description: 'Normalize data ranges with custom and preset projection options (30, 60, 90 days).'
    },
    {
      icon: <NoteAdd />,
      title: 'Collaborative Notes',
      description: 'Save and share observations on trends and findings with facility team members.'
    }
  ];

  const features = [
    {
      title: 'Real-Time Dashboards',
      description: 'Monitor admissions, transfers, and resident flow in real time. Our dynamic dashboard views include trend analysis, layered smart filters, and immediate drill-down options.',
      icon: <Dashboard className={styles.featureIcon} />,
      stats: 'Live Updates'
    },
    {
      title: 'Automated Reporting & Alerts',
      description: 'Generate and automate customized reports to identify risks, monitor improvements, and optimize facility performance. Set smart alerts so you\'re never caught off guard.',
      icon: <Analytics className={styles.featureIcon} />,
      stats: 'Smart Alerts'
    },
    {
      title: 'Enterprise Security',
      description: 'HIPAA-compliant platform ensuring the highest standards of data security and patient privacy protection.',
      icon: <Security className={styles.featureIcon} />,
      stats: 'HIPAA Compliant'
    },
    {
      title: 'Multi-Facility Management',
      description: 'Oversee multiple facilities from a centralized platform with full role-based access. Compare performance trends across facilities and pinpoint exactly where one excels or where support is needed.',
      icon: <Group className={styles.featureIcon} />,
      stats: 'Centralized Control'
    }
  ];

  const dashboardFeatures = [
    {
      title: 'Census Intelligence at a Glance',
      description: 'Visualize overall resident flow, track admissions and discharges, and spot census drops in real time.',
      image: '/assets/template/census_intelligence.png' // Placeholder
    },
    {
      title: 'Smart Trend Detection',
      description: 'Drill into root causes—whether it\'s a decline in admissions, rise in unplanned transfers, or a shift in diagnosis trends. Use advanced filters to isolate patterns by shift, payer type, diagnosis, or team.',
      image: '/assets/template/trend_detection.png' // Placeholder
    },
    {
      title: 'Proactive Notifications',
      description: 'Run automated reports or set up custom alerts that notify you the moment a trend begins—before it becomes a crisis.',
      image: '/assets/template/proactive_notifications.png' // Placeholder
    }
  ];

  const stats = [
    { number: '30%-60%', label: 'Reduction in Avoidable Hospital Transfers', icon: <TrendingUp /> },
    { number: 'Advanced', label: 'Customized Alerts, Trend Tracking & Multi-site Oversight', icon: <CheckCircle /> },
    { number: 'Track Every Step', label: 'Complete visibility into every resident\'s journey—across time, transitions, and touchpoints.', icon: <TrackEveryStepIcon /> },
            { number: 'Mobile', label: 'Access your data anywhere, anytime with our responsive mobile platform', icon: <Smartphone /> }
  ];

  return (
    <div className={styles.landingPage}>
      {/* Header with Login Button */}
      <Box className={styles.header}>
        <Container maxWidth="lg">
          <Grid container justifyContent="space-between" alignItems="center" sx={{ py: 2 }}>
            <Grid item>
              <div className={styles.logoContainer}>
                <TempLogo />
              </div>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="large"
                onClick={handleLoginClick}
                className={styles.headerLoginButton}
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                  px: { xs: 2, sm: 3, md: 4 },
                  py: { xs: 1, sm: 1.5 }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                  Login to Platform
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                  Login
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box className={styles.heroSection}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Fade in={isVisible} timeout={1000}>
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              sx={{ 
                minHeight: '80vh', 
                textAlign: 'center', 
                position: 'relative', 
                zIndex: 2,
                pt: { xs: 4, sm: 6, md: 8 },
                pb: { xs: 4, sm: 6, md: 8 }
              }}
            >
              <Grid item sx={{ mb: 4, width: '100%' }}>
                <Slide direction="down" in={isVisible} timeout={1200}>
                  <Typography variant="h1" component="h1" className={styles.heroTitle}>
                    Transform Your Healthcare
                    <br />
                    <span className={styles.heroTitleAccent}>Data Management</span>
                  </Typography>
                </Slide>

                <Slide direction="up" in={isVisible} timeout={1400}>
                  <Typography variant="h4" component="h2" className={styles.heroSubtitle} sx={{ mt: 3, mb: 4 }}>
                    Streamline Operations • Improve Outcomes • Enhance Efficiency
                  </Typography>
                </Slide>

                <Fade in={isVisible} timeout={1600}>
                  <Typography variant="body1" className={styles.heroDescription} sx={{ mb: 4, maxWidth: 900 }}>
                    A comprehensive healthcare management platform designed for skilled nursing facilities.
                    Track all resident transitions including admissions, hospitalizations, and community transfers-and generate actionable insights to improve operational efficiency and patient care quality with real-time dashboards and reporting tools.
                  </Typography>
                </Fade>

                <Zoom in={isVisible} timeout={1800}>
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={{ xs: 2, sm: 3 }} 
                    justifyContent="center" 
                    alignItems="center"
                    sx={{ 
                      mt: 4,
                      width: '100%',
                      maxWidth: { xs: '100%', sm: '600px' },
                      mx: 'auto'
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleOpenContactDialog}
                      className={styles.primaryCtaButton}
                      sx={{ 
                        px: { xs: 2, sm: 3, md: 4 }, 
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                        minWidth: { xs: '180px', sm: '200px', md: 'auto' },
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { xs: '260px', sm: 'none' },
                        height: { xs: '48px', sm: '56px' },
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Get a Personalized Walkthrough
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Get Walkthrough
                      </Box>
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleOpenVideoDialog}
                      startIcon={<PlayArrow sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />}
                      className={styles.secondaryButton}
                      sx={{ 
                        px: { xs: 2, sm: 3, md: 4 }, 
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                        minWidth: { xs: '180px', sm: '200px', md: 'auto' },
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { xs: '260px', sm: 'none' },
                        height: { xs: '48px', sm: '56px' },
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '& .MuiButton-startIcon': {
                          mr: { xs: 0.5, sm: 1 }
                        }
                      }}
                    >
                      Watch Demo
                    </Button>
                  </Stack>
                </Zoom>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ py: 6 }}>
          <Grid container spacing={4} alignItems="stretch">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index} sx={{ display: 'flex' }}>
                <Slide direction="up" in={isVisible} timeout={800 + index * 200}>
                  <div style={{ width: '100%', display: 'flex' }}>
                    <Zoom in={isVisible} timeout={1200 + index * 200}>
                      <Card
                        className={styles.statCard}
                        sx={{
                          width: '100%',
                          height: 260,
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <CardContent
                          sx={{
                            textAlign: 'center',
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Box className={styles.statIcon} sx={{ mb: 2, flexShrink: 0 }}>
                            {stat.icon}
                          </Box>
                          <Typography variant="h3" className={styles.statNumber} 
                          sx={{ 
                            mb: 1, 
                            flexShrink: 0,
                           }}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={styles.statLabel}
                            sx={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              lineHeight: 1.1,
                              fontSize: '0.875rem'                              
                            }}
                          >
                            {stat.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Zoom>
                  </div>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: 8 }}>
          <Typography variant="h2" component="h2" className={styles.sectionTitle} textAlign="center" sx={{ mb: 2 }}>
            Powerful Features for Healthcare Excellence
          </Typography>
          <Typography variant="h6" className={styles.sectionSubtitle} textAlign="center" sx={{ mb: 6 }}>
            Everything you need to manage your healthcare facility efficiently
          </Typography>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Slide direction={index % 2 === 0 ? 'right' : 'left'} in={isVisible} timeout={1000 + index * 200}>
                  <Card className={styles.featureCard} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box className={styles.featureHeader}>
                        <Box className={styles.featureIconContainer}>
                          {feature.icon}
                        </Box>
                        <Chip
                          label={feature.stats}
                          className={styles.featureChip}
                          size="small"
                        />
                      </Box>
                      <Typography variant="h5" component="h3" className={styles.featureTitle} sx={{ mb: 2 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" className={styles.featureDescription}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Dashboard Screenshots Section */}
        <Box sx={{ py: 8 }}>
          <Typography variant="h2" component="h2" className={styles.sectionTitle} textAlign="center" sx={{ mb: 2 }}>
            Use Case Breakdown (Three-Part Feature Spotlight)
          </Typography>
          <Typography variant="h6" className={styles.sectionSubtitle} textAlign="center" sx={{ mb: 6 }}>
            {/* You can display this as a three-column layout, rotating feature card, or stacked section: */}
            &nbsp;
          </Typography>

          <Grid container spacing={4} alignItems="stretch">
            {dashboardFeatures.map((dashboard, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
                <Slide direction="up" in={isVisible} timeout={800 + index * 400}>
                  <div style={{ width: '100%', display: 'flex' }}>
                    <Zoom in={isVisible} timeout={1400 + index * 400}>
                      <Card
                        className={styles.dashboardCard}
                        sx={{
                          width: '100%',
                          height: 360,
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                          '&:hover': {
                            transform: 'translateY(-12px) scale(1.02)',
                            boxShadow: '0 20px 40px rgba(34, 89, 201, 0.2)',
                            '& .iconContainer': {
                              transform: 'scale(1.1) rotate(5deg)',
                            },
                            '& .titleText': {
                              color: '#2259c9',
                            }
                          }
                        }}
                      >
                        <Fade in={isVisible} timeout={1800 + index * 400}>
                          <Box
                            sx={{
                              height: 140,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f8f9fa',
                              borderBottom: '1px solid #e9ecef',
                              transition: 'all 0.3s ease-in-out'
                            }}
                          >
                            <Box sx={{ textAlign: 'center' }}>
                              <Box
                                className="iconContainer"
                                sx={{
                                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                  display: 'inline-block'
                                }}
                              >
                                {index === 0 && <Analytics sx={{ fontSize: 60, color: '#2259c9', mb: 1 }} />}
                                {index === 1 && <TrendingUp sx={{ fontSize: 60, color: '#2259c9', mb: 1 }} />}
                                {index === 2 && <Schedule sx={{ fontSize: 60, color: '#2259c9', mb: 1 }} />}
                              </Box>
                              <Slide direction="up" in={isVisible} timeout={2200 + index * 400}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: '#6c757d',
                                    fontWeight: 500,
                                    transition: 'all 0.3s ease-in-out'
                                  }}
                                >
                                  {index === 0 && 'Real-Time Analytics'}
                                  {index === 1 && 'Trend Analysis'}
                                  {index === 2 && 'Alert System'}
                                </Typography>
                              </Slide>
                            </Box>
                          </Box>
                        </Fade>
                        <Fade in={isVisible} timeout={2400 + index * 400}>
                          <CardContent
                            sx={{
                              p: 3,
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Box>
                              <Typography
                                variant="h6"
                                className={`${styles.dashboardTitle} titleText`}
                                sx={{
                                  mb: 2,
                                  fontWeight: 600,
                                  transition: 'all 0.3s ease-in-out'
                                }}
                              >
                                {dashboard.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                className={styles.dashboardDescription}
                                sx={{
                                  lineHeight: 1.5,
                                  transition: 'all 0.3s ease-in-out'
                                }}
                              >
                                {dashboard.description}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Fade>
                      </Card>
                    </Zoom>
                  </div>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className={styles.toolsSection}>
          <Container maxWidth="lg" sx={{ py: 8 }}>
            <Box textAlign="center" sx={{ mb: 6 }}>
              <Slide direction="up" in={isVisible} timeout={1000}>
                <Typography variant="h3" component="h2" className={styles.sectionTitle} sx={{ mb: 2 }}>
                  Additional Specialized Tools
                </Typography>
              </Slide>
            </Box>

            <Grid container spacing={4}>
              {additionalTools.map((tool, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Zoom in={isVisible} timeout={1000 + index * 200}>
                    <Card className={styles.toolCard}>
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Box className={styles.toolIconContainer} sx={{ mb: 3 }}>
                          {tool.icon}
                        </Box>
                        <Typography variant="h6" className={styles.toolTitle} sx={{ mb: 2 }}>
                          {tool.title}
                        </Typography>
                        <Typography variant="body2" className={styles.toolDescription}>
                          {tool.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>      

        {/* Call to Action Section */}
        <Paper className={styles.finalCtaSection} sx={{ p: 8, mb: 8, textAlign: 'center' }}>
          <Zoom in={isVisible} timeout={2000}>
            <Box>
              <Typography variant="h3" component="h2" className={styles.finalCtaTitle} sx={{ mb: 2 }}>
                Custom Platforms for Complex Care.
              </Typography>
              <Typography variant="h6" className={styles.finalCtaDescription} sx={{ mb: 4 }}>
                We'll show you how our customizable platform can fit your organization's needs—whether you're in skilled nursing or beyond.
              </Typography>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={3} 
                sx={{ mt: 3 }} 
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleBuildPlatformClick}
                  className={styles.primaryCtaButton}
                  sx={{ 
                    px: { xs: 4, sm: 6 }, 
                    py: 2,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    minWidth: { xs: '200px', sm: 'auto' },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '280px', sm: 'none' }
                  }}
                >
                  Build Your Platform
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleAskQuestionClick}
                  className={styles.secondaryButton}
                  sx={{ 
                    px: { xs: 4, sm: 6 }, 
                    py: 2,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    minWidth: { xs: '200px', sm: 'auto' },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '280px', sm: 'none' }
                  }}
                >
                  Ask a Question?
                </Button>
              </Stack>
            </Box>
          </Zoom>
        </Paper>
      </Container>

      {/* Video Demo Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleCloseVideoDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: '#000'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#000',
            color: 'white',
            pb: 1
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Platform Demo - See SNF Data Solutions in Action
          </Typography>
          <IconButton
            onClick={handleCloseVideoDialog}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: '#000',
            position: 'relative',
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden'
          }}
        >
          <Box
            component="video"
            src="/assets/landing/SimpleSNF_Overview.mp4"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title="SNF Data Solutions Platform Demo"
            controls
            autoPlay
            muted
            loop
          />
        </DialogContent>
      </Dialog>

      {/* Contact Form Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            maxHeight: { xs: '95vh', sm: '90vh' },
            width: { xs: '95%', sm: '90%', md: 'auto' },
            mx: { xs: 'auto', sm: 'auto' },
            my: { xs: '2.5vh', sm: '5vh' }
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: '8px', sm: '16px' }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 0,
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 }
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#2259c9', 
              fontWeight: 600,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
              lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 }
            }}
          >
            Get Your Personalized Walkthrough
          </Typography>
          <IconButton
            onClick={handleCloseContactDialog}
            sx={{
              color: '#6c757d',
              '&:hover': { backgroundColor: '#f8f9fa' },
              p: { xs: 0.5, sm: 1 }
            }}
          >
            <Close sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          pt: { xs: 1, sm: 1 },
          px: { xs: 1, sm: 2, md: 3 },
          pb: { xs: 1, sm: 2, md: 3 }
        }}>
          <ContactForm
            title=""
            subtitle="Tell us about your organization and we'll show you exactly how our platform can work for you."
            showIcon={false}
            onSuccess={handleContactFormSuccess}
            onError={handleContactFormError}
          />
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage; 